<template>
  <div class="image-uploader" :class="variant === 'small' && 'image-uploader--small'" :style="rootStyle">
    <a-upload
      ref="uploader"
      name="file"
      accept="image/*"
      :showUploadList="false"
      :beforeUpload="beforeImageUpload"
      @change="handleImageUpload"
    >
      <div class="upload-container">
        <div v-if="value" class="upload-preview">
          <img :src="value" />
          <div
            v-if="hasDeleteOverlay"
            class="overlay"
            :class="imageDeleteConfirmVisible && 'show'"
            @click.stop
          >
            <a-button v-if="showEditButton" type="link" @click.stop="editImage">
              <a-icon type="edit" class="icon" />
            </a-button>
            <a-popconfirm
              title="Do you really want to delete it?"
              okText="Yes"
              cancelText="No"
              @confirm.stop="removeImage"
              @visibleChange="(val) => (imageDeleteConfirmVisible = val)"
            >
              <a-icon type="delete" class="icon delete-icon" @click.stop />
            </a-popconfirm>
          </div>
        </div>
        <div v-else class="upload-placeholder">
          <a-icon v-if="loading" type="loading" class="text-primary" style="font-size: 24px" />
          <a-icon v-else-if="variant === 'small'" type="plus" class="text-primary" style="font-size: 24px" />
          <a-icon v-else type="inbox" class="text-primary" style="font-size: 48px" />
          <div class="ant-upload-text-container">
            <p class="ant-upload-text">
              {{ variant === 'small' ? 'Upload' : 'Click or drag file to this area to upload' }}
            </p>
            <p class="ant-upload-hint">
              File size {{ fileWidth }} x {{ fileHeight }} px
            </p>
          </div>
        </div>
      </div>
    </a-upload>
  </div>
</template>
<script>
import { getImageDimensions, getBase64 } from '@/services/utils/image'

export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    hasDeleteOverlay: {
      type: Boolean,
      default: false,
    },
    showEditButton: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: 10,
    },
    fileWidth: {
      type: Number,
      default: 1160,
    },
    fileHeight: {
      type: Number,
      default: 414,
    },
    aspectRatio: {
      type: String,
    },
    variant: {
      type: String,
      default: 'regular',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    containerWidth: {
      type: Number,
    },
    containerHeight: {
      type: Number,
    },
  },
  data() {
    return {
      imageDeleteConfirmVisible: false,
    }
  },
  computed: {
    rootStyle() {
      const res = {}
      if (this.aspectRatio) {
        res['aspect-ratio'] = this.aspectRatio
      }

      if (this.containerWidth) {
        res.width = this.containerWidth + 'px'
      }

      if (this.containerHeight) {
        res.height = this.containerHeight + 'px'
      }

      return res
    },
  },
  methods: {
    async validateUploadedImage(file) {
      if (!file) {
        return false
      }

      const isImage = file.type.split('/')[0] === 'image'
      const fileExtension = file.type.split('/')[1]
      if (!isImage) {
        this.$notification.error({
          message: 'You can only upload WEBP, PNG or JPEG file!',
        })
        return false
      }
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        this.$notification.error({
          message: 'Image must be smaller than 10MB!',
        })
        return false
      }
      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        if (width !== this.fileWidth || height !== this.fileHeight) {
          this.$notification.error({
            message: `Image must be ${this.fileWidth}x${this.fileHeight}px!`,
          })
          return false
        }
        if (
          fileExtension !== 'png' &&
          fileExtension !== 'webp' &&
          fileExtension !== 'jpeg'
        ) {
          this.$notification.error({
            message: 'Header image must have WEBP, PNG or JPEG extension!',
          })
          return false
        }
      }
      return file
    },
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('You can only upload image files!')
      }
      return isImage
    },
    async handleImageUpload(event) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedImage(file.originFileObj)

      if (!isFileValid) {
        return
      }

      const imageFile = file.originFileObj
      const imageUrl = await getBase64(file.originFileObj)

      this.$emit('input', imageUrl)
      this.$emit('change', { file: imageFile, url: imageUrl })
    },
    removeImage() {
      this.imageDeleteConfirmVisible = false

      this.$emit('input', '')
      this.$emit('change', { file: null, url: '' })
    },
    editImage() {
      const uploadComponent = this.$refs.uploader
      if (uploadComponent) {
        const fileInput =
          uploadComponent.$el.querySelector('input[type="file"]')

        if (fileInput) {
          fileInput.click()
        }
      }
    },
  },
}
</script>
<style lang="scss">
.image-uploader {
  position: relative;
  max-width: 368px;

  & .ant-upload-select {
    width: 100%;
    height: 100%;
  }
}

.upload-container {
  width: 100%;
  height: 100%;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 16px;
  border: 1px dashed #d9d9d9;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #1890ff;
  }

  .ant-upload-hint {
    font-size: 14px;
    line-height: 22px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  .has-error & {
    border-color: #f5222e;
  }
}

.upload-preview {
  position: relative;
  display: block;
  width: 100%;

  img {
    width: 100%;
    display: block;
  }

  &:hover .overlay {
    display: flex;
  }

  .overlay {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    gap: 8px;
    background: rgba(0, 0, 0, 0.3);
    align-items: center;
    justify-content: center;

    &.show {
      display: flex;
    }

    .ant-btn {
      padding: 0;
      border: none;
      margin-top: 2px;
    }
  }

  .icon {
    font-size: 20px;
    color: #fff;
    cursor: pointer;

    &:hover,
    &:focus {
      color: #fff !important;
    }
  }
}

.image-uploader--small {
  width: 120px;
  height: 120px;

  .ant-upload-hint {
    font-size: 10px;
    line-height: 19px;
  }
}
</style>
